// src/components/CrimeRegistration.jsx
import React, { useState } from 'react';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  CircularProgress,
  IconButton,
  Paper,
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { registerCrime } from '../../features/crimeSlice'; // We'll update slice below
import { validateFields } from '../../services/validate';
import { useSnackbar } from 'notistack';
import DynamicBreadcrumbs from '../layout/Breadcrumb';
import Layout from '../layout/Layout';

const CrimeRegistration = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { status } = useSelector((state) => state.crime);

  const [formData, setFormData] = useState({
    crimeType: '',
    crimeDate: '',
    state: 'Uttar Pradesh', // Pre-filled
    district: '',
    tehsil: '',
    villageTown: '',
    policeStation: '',
    description: '',
    reason: '',
    offenders: [{ crimeNumber: '', role: '' }],
    victims: [{ name: '', aadhar: '', mobile: '' }],
    eyeWitnesses: [{ name: '', mobile: '' }],
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const handleArrayChange = (arrayName, index, field, value) => {
    const updated = formData[arrayName].map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    setFormData({ ...formData, [arrayName]: updated });
  };

  const addArrayItem = (arrayName, defaultItem) => {
    setFormData({ ...formData, [arrayName]: [...formData[arrayName], defaultItem] });
  };

  const removeArrayItem = (arrayName, index) => {
    if (formData[arrayName].length === 1) return; // Keep at least one
    const updated = formData[arrayName].filter((_, i) => i !== index);
    setFormData({ ...formData, [arrayName]: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation (you can extend validateFields)
    if (!formData.crimeType || !formData.crimeDate || formData.offenders.length === 0) {
      enqueueSnackbar('Crime type, date, and at least one offender are required', {
        variant: 'error',
      });
      return;
    }

    try {
      const response = await dispatch(registerCrime(formData)).unwrap();
      enqueueSnackbar('Incident registered successfully', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err || 'Failed to register incident', {
        variant: 'error',
      });
    }
  };

  return (
    <Layout title="Register Incident" description="Uttar Pradesh Police Crime Records">
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <DynamicBreadcrumbs />

        <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
          <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 600 }}>
            Register New Incident / Crime
          </Typography>

          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
            Enter details of the incident and add all involved offenders, victims, and witnesses.
          </Typography>

          <form onSubmit={handleSubmit}>
            {/* Basic Incident Info */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Crime Type"
                  name="crimeType"
                  value={formData.crimeType}
                  onChange={handleChange}
                  error={!!errors.crimeType}
                  helperText={errors.crimeType}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Crime Date"
                  type="date"
                  name="crimeDate"
                  InputLabelProps={{ shrink: true }}
                  value={formData.crimeDate}
                  onChange={handleChange}
                  error={!!errors.crimeDate}
                  helperText={errors.crimeDate}
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  disabled // Pre-filled
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="District"
                  name="district"
                  value={formData.district}
                  onChange={handleChange}
                  error={!!errors.district}
                  helperText={errors.district}
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Tehsil"
                  name="tehsil"
                  value={formData.tehsil}
                  onChange={handleChange}
                  error={!!errors.tehsil}
                  helperText={errors.tehsil}
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Village / Town"
                  name="villageTown"
                  value={formData.villageTown}
                  onChange={handleChange}
                  error={!!errors.villageTown}
                  helperText={errors.villageTown}
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Police Station"
                  name="policeStation"
                  value={formData.policeStation}
                  onChange={handleChange}
                  error={!!errors.policeStation}
                  helperText={errors.policeStation}
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Incident Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Reason / Motive"
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  sx={{ mb: 3 }}
                />
              </Grid>
            </Grid>

            {/* Offenders Section */}
            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              Offenders (add all involved persons)
            </Typography>
            {formData.offenders.map((offender, index) => (
              <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      label="Enter Crime Number"
                      value={offender.crimeNumber}
                      onChange={(e) =>
                        handleArrayChange('offenders', index, 'crimeNumber', e.target.value)
                      }
                      helperText="Must be already registered person"
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      label="Role (optional)"
                      value={offender.role}
                      onChange={(e) =>
                        handleArrayChange('offenders', index, 'role', e.target.value)
                      }
                      placeholder="Main offender, Accomplice, etc."
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <IconButton
                      color="error"
                      onClick={() => removeArrayItem('offenders', index)}
                      disabled={formData.offenders.length === 1}
                    >
                      <RemoveIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={() => addArrayItem('offenders', { crimeNumber: '', role: '' })}
              variant="outlined"
              sx={{ mb: 3 }}
            >
              Add Another Offender
            </Button>

            {/* Victims Section */}
            <Typography variant="h6" gutterBottom>
              Victims
            </Typography>
            {formData.victims.map((victim, index) => (
              <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Name"
                      value={victim.name}
                      onChange={(e) => handleArrayChange('victims', index, 'name', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Aadhaar (optional)"
                      value={victim.aadhar}
                      onChange={(e) => handleArrayChange('victims', index, 'aadhar', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Mobile (optional)"
                      value={victim.mobile}
                      onChange={(e) => handleArrayChange('victims', index, 'mobile', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <IconButton
                      color="error"
                      onClick={() => removeArrayItem('victims', index)}
                      disabled={formData.victims.length === 1}
                    >
                      <RemoveIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={() => addArrayItem('victims', { name: '', aadhar: '', mobile: '' })}
              variant="outlined"
              sx={{ mb: 3 }}
            >
              Add Victim
            </Button>

            {/* Witnesses Section */}
            <Typography variant="h6" gutterBottom>
              Eye Witnesses
            </Typography>
            {formData.eyeWitnesses.map((witness, index) => (
              <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Name"
                      value={witness.name}
                      onChange={(e) => handleArrayChange('eyeWitnesses', index, 'name', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      label="Mobile (optional)"
                      value={witness.mobile}
                      onChange={(e) => handleArrayChange('eyeWitnesses', index, 'mobile', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <IconButton
                      color="error"
                      onClick={() => removeArrayItem('eyeWitnesses', index)}
                      disabled={formData.eyeWitnesses.length === 1}
                    >
                      <RemoveIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={() => addArrayItem('eyeWitnesses', { name: '', mobile: '' })}
              variant="outlined"
              sx={{ mb: 4 }}
            >
              Add Witness
            </Button>

            {/* Submit */}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              disabled={status === 'loading'}
              sx={{ py: 1.5, fontSize: '1.1rem' }}
            >
              {status === 'loading' ? (
                <>
                  <CircularProgress size={24} color="inherit" sx={{ mr: 2 }} />
                  Registering Incident...
                </>
              ) : (
                'Register Incident'
              )}
            </Button>
          </form>
        </Paper>
      </Container>
    </Layout>
  );
};

export default CrimeRegistration;